import { useEffect, useState } from "react";
import api from "@/lib/api";

const READINESS = {
  ready:        { label: "Ready",        cls: "bg-[#0B3D2E] text-white" },
  nearly_there: { label: "Nearly there", cls: "bg-[#a05a00]/15 text-[#a05a00]" },
  in_progress:  { label: "In progress",  cls: "bg-neutral-200 text-neutral-600" },
  at_risk:      { label: "At risk",      cls: "bg-[#8B2E2E]/15 text-[#8B2E2E]" },
};

const cell = (v, suffix = "") => (v === null || v === undefined ? "—" : `${v}${suffix}`);

export default function ScorecardTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    api.get("/admin/scorecard").then(r => setData(r.data)).catch(e => setError(e.response?.data?.detail || e.message));
  };
  useEffect(load, []);

  if (error) return (
    <div className="border hairline bg-white p-5 text-sm" data-testid="scorecard-error">
      <span className="text-[#8B2E2E]">Could not load the scorecard: {String(error)}</span>
      <button className="btn-ghost text-xs ml-3" onClick={load} data-testid="scorecard-retry">Retry</button>
    </div>
  );
  if (!data) return <div className="text-sm text-neutral-500" data-testid="scorecard-loading">Building scorecard…</div>;

  return (
    <div className="space-y-8" data-testid="scorecard-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {Object.entries(READINESS).map(([key, r]) => (
          <div key={key} className="border hairline bg-white p-5" data-testid={`scorecard-summary-${key}`}>
            <div className="font-display text-3xl leading-none">{data.summary[key] ?? 0}</div>
            <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 mt-2 ${r.cls}`}>{r.label}</span>
          </div>
        ))}
      </div>

      <div className="border hairline bg-white overflow-x-auto">
        <div className="p-4 border-b hairline flex items-baseline justify-between">
          <div className="label-eyebrow">Manager scorecard · {data.trainees.length} trainee{data.trainees.length !== 1 ? "s" : ""}</div>
          <div className="font-mono text-xs text-neutral-500">Lessons: {data.total_lessons} · Readiness = lessons 40% · sim 25% · role-play 20% · lab 15%</div>
        </div>
        {data.trainees.length === 0 ? (
          <div className="p-4 text-sm text-neutral-500" data-testid="scorecard-empty">No trainees in this workspace yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="scorecard-table">
            <thead className="bg-[color:var(--brand-surface2)]">
              <tr className="text-left label-eyebrow">
                <th className="p-3">Trainee</th>
                <th className="p-3">Lessons</th>
                <th className="p-3">Role-play avg</th>
                <th className="p-3">Sim best</th>
                <th className="p-3">Lab</th>
                <th className="p-3">Inactive</th>
                <th className="p-3">Readiness</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.trainees.map(t => {
                const r = READINESS[t.readiness] || READINESS.in_progress;
                return (
                  <tr key={t.id} className="border-t hairline" data-testid={`scorecard-row-${t.id}`}>
                    <td className="p-3">
                      <div className="font-medium">{t.name || t.email}</div>
                      <div className="text-xs text-neutral-500">{[t.position, t.department].filter(Boolean).join(" · ") || t.email}</div>
                    </td>
                    <td className="p-3">
                      <span className="font-display">{t.percent}%</span>
                      <span className="text-xs text-neutral-500 ml-1">({t.completed}/{t.total_lessons})</span>
                    </td>
                    <td className="p-3" data-testid={`scorecard-roleplay-${t.id}`}>{cell(t.avg_roleplay, "/100")}</td>
                    <td className="p-3" data-testid={`scorecard-sim-${t.id}`}>{cell(t.sim_score, "/100")}</td>
                    <td className="p-3" data-testid={`scorecard-lab-${t.id}`}>{cell(t.lab_score, "/10")}</td>
                    <td className="p-3 text-neutral-600">{cell(t.days_inactive, "d")}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-display text-lg" data-testid={`scorecard-score-${t.id}`}>{t.readiness_score}</span>
                        <div className="w-16 h-1.5 bg-[color:var(--brand-surface2)]">
                          <div className="h-full bg-[color:var(--brand-primary)]" style={{ width: `${t.readiness_score}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 ${r.cls}`} data-testid={`scorecard-status-${t.id}`}>{r.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
